import type { RefreshConfigRemoveSizes } from "../config/platforms/types";

export enum Comparator {
  EXACT = "EXACT",
  LESS_THAN = "LESS_THAN",
  LESS_THAN_OR_EQUAL = "LESS_THAN_OR_EQUAL",
  GREATER_THAN = "GREATER_THAN",
  GREATER_THAN_OR_EQUAL = "GREATER_THAN_OR_EQUAL",
}

/**
 * Default configuration for removing sizes after an ad's creative is fully loaded on the page.
 *
 * This configuration is used when the `removeSizes` option is enabled
 * and no custom configuration is provided. It specifies the strategy
 * for removing sizes, the dimension to match, and the comparator to use.
 *
 * - `strategy`: Determines how often sizes should be removed. The default value of "once" means it'll be removed after the first ad loads.
 * - `match`: Specifies which dimension (height or width) to match from the loaded creative's dimensions when removing sizes. Height is used by default.
 * - `with`: Defines the comparator to use for matching sizes. The default is `GREATER_THAN`. Only ad slot sizes which have a height greater than the loaded creative's height will be removed.
 */
export const DEFAULT_REMOVE_SIZES_CONFIG: RefreshConfigRemoveSizes = {
  strategy: "once",
  match: "height",
  with: Comparator.GREATER_THAN,
};

/**
 * Returns a comparator function based on the specified comparator type.
 *
 * @param comparator - The comparison operator to use.
 *                   It can be one of the following:
 * - `Comparator.EXACT`
 * - `Comparator.LESS_THAN`
 * - `Comparator.LESS_THAN_OR_EQUAL`
 * - `Comparator.GREATER_THAN`
 * - `Comparator.GREATER_THAN_OR_EQUAL`
 * @returns A function that takes two numbers and returns a boolean indicating
 *          whether the comparison is true based on the specified comparator.
 * @throws Error if the comparator is invalid
 */
function getComparatorFunction(
  comparator: Comparator,
): (a: number, b: number) => boolean {
  switch (comparator) {
    case Comparator.EXACT:
      return (a, b) => a === b;
    case Comparator.LESS_THAN:
      return (a, b) => a < b;
    case Comparator.LESS_THAN_OR_EQUAL:
      return (a, b) => a <= b;
    case Comparator.GREATER_THAN:
      return (a, b) => a > b;
    case Comparator.GREATER_THAN_OR_EQUAL:
      return (a, b) => a >= b;
    default:
      throw new Error(`Invalid comparator: ${comparator}`);
  }
}

export type SizeTuple = [number, number];

/**
 * Check if the `size` is a valid tuple of numbers.
 *
 * @param size - The size to check.
 * @returns `true` if the size is a valid tuple of numbers, `false` otherwise.
 */
function isValidSize(size: googletag.GeneralSize): size is SizeTuple {
  return (
    Array.isArray(size) &&
    size.length === 2 &&
    typeof size[0] === "number" &&
    typeof size[1] === "number"
  );
}

/**
 * Check if the size is a valid tuple of numbers and not [0, 0]. Extends {@link isValidSize}.
 *
 * @param size - The size to check.
 * @returns `true` if the size is a valid tuple of numbers, `false` otherwise.
 * @throws Error if the size is [0, 0].
 */
function isValidSizeToCompare(size: googletag.GeneralSize): size is SizeTuple {
  if (!isValidSize(size)) {
    return false;
  }
  // passing down two wildcards (0) in `size` isn't supported
  if (size[0] === 0 && size[1] === 0) {
    throw new Error("Invalid size to compare [0,0]");
  }
  return true;
}

/**
 * Determines if the given slot size matches the specified size to compare with,
 * based on the provided comparator.
 *
 * @param slotSize - The size of the slot to check. It should be a tuple of two numbers
 * representing the width and height.
 * @param sizeToCompare - A tuple of two numbers representing the size to compare against.
 * A value of `0` in either dimension acts as a wildcard, meaning that dimension will not be compared.
 * @param comparator - The comparison operator to use when comparing the slot size
 * with the size to compare. Defaults to `Comparator.EXACT`.
 * It can be one of the following:
 * - `Comparator.EXACT`
 * - `Comparator.LESS_THAN`
 * - `Comparator.LESS_THAN_OR_EQUAL`
 * - `Comparator.GREATER_THAN`
 * - `Comparator.GREATER_THAN_OR_EQUAL`
 *
 * Alternatively, a custom comparator function can be provided. The function should
 * take two numbers as arguments and return a boolean indicating whether the comparison is true.
 *
 * @returns `true` if the slot size matches the size to compare based on the comparator,
 * `false` otherwise.
 * @throws Error if the comparator is invalid or if the `sizeToCompare` is `[0, 0]`.
 */
export function isSlotOfSize(
  slotSize: SizeTuple,
  sizeToCompare: SizeTuple,
  comparator:
    | Comparator
    | ((a: number, b: number) => boolean) = Comparator.EXACT,
) {
  // check if `slotSize` and `sizeToCompare` is a valid tuple of numbers, if not, return false
  if (!isValidSize(slotSize) || !isValidSizeToCompare(sizeToCompare)) {
    return false;
  }

  const compare =
    typeof comparator === "function"
      ? comparator
      : getComparatorFunction(comparator);

  const [slotWidth, slotHeight] = slotSize;
  const [compareWidth, compareHeight] = sizeToCompare;

  // check only [1], if `compareWidth` is 0
  if (compareWidth === 0) {
    return compare(slotHeight, compareHeight);
  }

  // check only [0], if `compareHeight` is 0
  if (compareHeight === 0) {
    return compare(slotWidth, compareWidth);
  }

  // check both [0] and [1], if `compareWidth` and `compareHeight` are not 0
  return compare(slotWidth, compareWidth) && compare(slotHeight, compareHeight);
}

/**
 * Deletes slot sizes that match the specified size to compare with,
 * based on the provided comparator.
 *
 * @param sizes - The sizes to check. It has to be array of sizes.
 * @param sizeToCompare - A tuple of two numbers representing the size to compare against.
 *                        A value of `0` in either dimension acts as a wildcard, meaning
 *                       that dimension will not be compared.
 * @param comparator - The comparison operator to use when comparing the slot size
 *                    with the size to compare. Defaults to `Comparator.EQUAL`.
 * @returns A new array of sizes with the matching sizes removed or the original `sizes` if it is not an array.
 * @throws Error if the comparator is invalid or if the `sizeToCompare` is `[0, 0]`.
 *
 * @example
 * ```ts
 * const sizes = [[300, 250], [200, 200], [728, 90]];
 * const sizeToCompare = [300, 250];
 * const comparator = Comparator.EXACT;
 * const result = deleteSlotSizes(sizes, sizeToCompare, comparator);
 * /// result = [[200, 200], [728, 90]]
 * ```
 *
 * @example
 * ```ts
 * const sizes = [[300, 250], [200, 200], [728, 90]];
 * const sizeToCompare = [0, 250];
 * const comparator = Comparator.LESS_THAN;
 * const result = deleteSlotSizes(sizes, sizeToCompare, comparator);
 * /// result = [[300, 250]]
 */
export function deleteSlotSizes(
  sizes: googletag.GeneralSize,
  sizeToCompare: SizeTuple,
  comparator: Comparator = Comparator.EXACT,
): googletag.GeneralSize {
  if (!Array.isArray(sizes)) {
    return sizes;
  }

  const compare = getComparatorFunction(comparator);

  return (sizes as googletag.GeneralSize[]).filter((size) => {
    return !isSlotOfSize(size as SizeTuple, sizeToCompare, compare);
  }) as googletag.GeneralSize;
}

/**
 * Takes the reponsize size mapping from an AdNode and adds the provided size
 * to all of the responsive sizes.
 *
 * For example:
 *
 * ```ts
 * const responsiveSizes = [
 *   [[728, 0], [[728, 90], [300, 250]]],
 *   [[0, 0], [[300, 250]]]
 * ];
 * const newSize = [160, 600];
 * const updatedSizes = addSizeToAllResponsiveSizes(responsiveSizes, newSize);
 * // updatedSizes: [
 * //   [[728, 0], [[728, 90], [300, 250], [160, 600]]],
 * //   [[0, 0], [[300, 250], [160, 600]]]
 * // ]
 * ```
 *
 * @param responsiveSizes The reponsiveSizes from an AdNode
 * @param size The size to add to all responsive sizes.
 * @returns The updated responsive sizes
 */
export function addSizeToAllResponsiveSizes(
  responsiveSizes: googletag.SizeMappingArray,
  size: SizeTuple,
): googletag.SizeMappingArray {
  for (const responsiveSize of responsiveSizes) {
    if (responsiveSize.length === 2) {
      const sizes = responsiveSize[1];
      (sizes as googletag.MultiSize).push(size);
    }
  }
  return responsiveSizes;
}
